import React, { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { loginAsAdmin } from "../../redux/auth/authSlice";
import { postApi } from "../../api/api_instance";
import { useNavigate } from "react-router-dom";
import { API_URLS } from "../../api/apiUrls";
import { Eye, EyeOff } from "lucide-react";
import PopupCard from "../../components/Cards/PopupCard/PopupCard";

export const LoginAdmin = React.memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showCard, setShowCard] = useState(true);

  const [popup, setPopup] = useState({
    visible: false,
    type: "", // success | warning | error
    title: "",
    message: "",
  });

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === "email") setEmail(value);
    if (name === "password") setPassword(value);
  }, []);

  const showPopup = (type, title, message) => {
    setPopup({ visible: true, type, title, message });
    setTimeout(() => {
      setPopup((prev) => ({ ...prev, visible: false }));
    }, 3000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      return showPopup(
        "warning",
        "Missing Fields",
        "Please enter both email and password"
      );
    }

    setIsLoading(true);
    try {
      const response = await postApi({
        apiUrl: API_URLS.LOGIN,
        data: { email, password },
      });

      if (response.success) {
        const { user, token } = response.data;
        dispatch(loginAsAdmin({ user, token }));

        localStorage.setItem(
          "postLoginPopup",
          JSON.stringify({
            type: "success",
            title: "Welcome to Carburn Admin",
            message: "Empowering your Net Zero journey",
          })
        );

        // Small timeout to ensure localStorage is set before navigation
        setTimeout(() => {
          navigate("/");
        }, 50);
      } else {
        showPopup(
          "error",
          "Login Failed",
          response.message || "Please check your credentials and try again."
        );
      }
    } catch (error) {
      showPopup(
        "error",
        "Login Error",
        error.message || "Login failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-12 overflow-hidden">
      <div
        className={`bg-white py-8 px-6 rounded-xl shadow-xl w-full max-w-md sm:px-10 border border-gray-200 transform transition-all duration-500 ${
          showCard ? "animate-fadeSlideIn" : "opacity-0 translate-y-8"
        }`}
      >
        <h2 className="text-center text-2xl font-bold text-[#06513D] mb-2">
          CARBURN ADMIN
        </h2>
        <p className="text-center text-sm text-gray-600 mb-8">
          Carbon Footprint & ESG Reporting
        </p>

        <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
          Welcome Back
        </h3>

        <form className="space-y-6" onSubmit={handleLogin}>
          {/* Email Field */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder="Enter your email"
              value={email}
              onChange={handleInputChange}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-[#06513D] sm:text-sm"
              disabled={isLoading}
            />
          </div>

          {/* Password Field */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>

              <button
                type="button"
                className="text-sm text-[#06513D]"
                onClick={() => navigate("/reset-password")}
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <input
                id="password"
                name="password"
                type={passwordVisible ? "text" : "password"}
                required
                placeholder="Enter your password"
                value={password}
                onChange={handleInputChange}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-[#06513D] sm:text-sm"
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setPasswordVisible(!passwordVisible)}
                disabled={isLoading}
              >
                {passwordVisible ? (
                  <EyeOff className="h-4 w-4 text-gray-400" />
                ) : (
                  <Eye className="h-4 w-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Remember Me */}
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading}
            />
            <label
              htmlFor="remember-me"
              className="ml-2 block text-sm text-gray-700"
            >
              Remember me
            </label>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-md text-sm font-medium text-white bg-[#06513D] hover:bg-[#054a36] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing In...
                </div>
              ) : (
                "Login as Admin"
              )}
            </button>
          </div>
        </form>
      </div>

      {popup.visible && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
          <PopupCard
            type={popup.type}
            title={popup.title}
            message={popup.message}
          />
        </div>
      )}

      <style>{`
        @keyframes fadeSlideIn {
          0% {
            opacity: 0;
            transform: translateY(40px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeSlideIn {
          animation: fadeSlideIn 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
});
